import React, { useEffect, useRef, useState } from 'react';
import OTP from 'components/OTP/OTP';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import { BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import Button from 'components/CustomButtons/Button';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import CustomInput from 'components/CustomInput/CustomInput';
import { LockOutlined } from '@material-ui/icons';
import { getLoginLabels } from 'redux/modules/dynamicLabels/selectors';
import { useSelector } from 'react-redux';

// @ts-ignore
const useStyles = makeStyles(styles);

const RESEND_DELAY = 3; // minutes

interface IOtpTab {
	otp: string;
	setOtp: React.Dispatch<React.SetStateAction<string>>;
	sendOtp: () => void;
	error?: string;
	inOneInput?: boolean;
	otpIncorrect?: boolean;
	maxOtpLength?: number;
	resendText?: string;
	resendDelay?: number;
	showResendOption?: boolean;
	showOTPNote?: boolean;
	submitOTP?: () => void;
}

const OtpTab = ({
	otp,
	setOtp,
	sendOtp,
	error = '',
	inOneInput = false,
	maxOtpLength,
	otpIncorrect = false,
	resendDelay = RESEND_DELAY,
	resendText = translate('number.settings.resend.otp'),
	showResendOption = false,
	showOTPNote = false,
	submitOTP,
}: IOtpTab) => {
	const timerRef = useRef<ReturnType<typeof setTimeout>>();
	const classes = useStyles();
	const [id, setId] = useState(+new Date());
	const [resendVisible, setResendVisible] = useState<boolean>(false);
	const loginLabels = useSelector(getLoginLabels);

	const resendOtp = (): void => {
		sendOtp();
		setId(+new Date());
		setResendVisible(false);
		startTimer();
	};

	const startTimer = () => {
		timerRef.current = setTimeout(() => setResendVisible(true), resendDelay * 60 * 1000);
	};

	useEffect(() => {
		startTimer();
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	return (
		<div className={inOneInput ? classes.otpTabWrapperWithLineInput : classes.otpTabWrapper}>
			<BodyS disableMargin>{translate('number.settings.password.sent')}</BodyS>
			<ConditionalRender
				key={id}
				show={inOneInput}
				onTrue={() => (
					<div>
						<CustomInput
							labelText={translate('number.settings.one.time.password')}
							id="otp_input"
							formControlProps={{
								fullWidth: true,
							}}
							error={false}
							icon={<LockOutlined />}
							inputProps={{
								onChange: (event: React.FormEvent<HTMLInputElement>) =>
									setOtp((event.target as HTMLInputElement).value),
								required: true,
								value: otp,
								type: 'text',
								autoComplete: 'off',
								maxLength: maxOtpLength,
							}}
						/>
					</div>
				)}
				onFalse={() => (
					<div className={classes.resendText}>
						<OTP
							key={`OTP_${id}`}
							otpValue={otp}
							setOtpValue={setOtp}
							otpIncorrect={otpIncorrect}
							expire_time={resendDelay}
							showResendOption={showResendOption}
							submitOTP={submitOTP}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={resendVisible}
				onTrue={() => (
					<div className={inOneInput ? '' : classes.resendText}>
						<BodyS inline>{translate('number.settings.have.not.received.text')}</BodyS>
						<Button color="tertiary-light" onClick={resendOtp}>
							{resendText}
						</Button>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!error}
				onTrue={() => (
					<div className={classes.errorBoxWrapper}>
						<ErrorBox text={error} />
					</div>
				)}
			/>
			{showOTPNote && <p className={classes.otp_note}>{loginLabels?.errors?.OTP_NOTE}</p>}
		</div>
	);
};

export default OtpTab;
